import Component from '@glimmer/component'
import { service } from '@ember/service'
import { colors } from '@colors'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type { IntlService } from 'ember-intl'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import { SeriesColumn } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import type Highcharts from 'highcharts'
import { t } from 'ember-intl'
import type { StorylandsTotalsDataItem } from 'teacher-ui-client/utils/student-events/fetchers/storylands-totals'
import { formatValue } from 'teacher-ui-client/utils/format-value'

function xAxisMonthFormatter(this: Highcharts.AxisLabelsFormatterContextObject) {
  const monthAndYear = this.value
  return convertDateFormat(monthAndYear as string)
}

function toolTipFormatter(this: Highcharts.Point) {
  return `${this.y}`
}

interface Args {
  title: string
  data: StorylandsTotalsDataItem
  summaryTitle: string
  grade: string
  tooltip: string
}

interface Signature {
  Args: Args
}

export class StorylandsLessonsTotals extends Component<Signature> {
  @service intl!: IntlService

  get summaryStats(): Stat[] {
    const { intl } = this
    const { summary } = this.args.data

    const total = summary.total_count
    const average = summary.average_score
    const totalLabel = intl.t('reporting.summaryTitles.storylandsTotals.total')
    const averageLabel = intl.t('reporting.summaryTitles.storylandsTotals.average')

    if (!total) return []

    return [
      { label: totalLabel, value: formatNumber(total, 0).toString() },
      { label: averageLabel, value: formatValue(average, { append: '%' }) },
    ]
  }

  get columns() {
    const monthsData = this.args.data?.months ?? []

    const data = monthsData.map((monthData, index) => {
      return { x: index, y: monthData.count }
    })

    return [{ name: 'count', color: colors.juicyOrange300, data, type: 'column' as const }]
  }

  get months() {
    return this.args.data?.months.map((monthData) => monthData.month) ?? []
  }

  <template>
    <div data-test-giraffe-chart-storylands class="relative h-full">
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{SummaryBoxColorScheme.Orange}}
          />
        </:left>
        <:right>
          <ChartInfoTooltip @tooltipContent={{@tooltip}} />
          <SeriesColumn
            @title={{@title}}
            @data={{this.columns}}
            @categories={{this.months}}
            @yAxisLabel={{t "reporting.yAxisLabels.storylandsTotals"}}
            @xAxisLabelFormatter={{xAxisMonthFormatter}}
            @toolTipFormatter={{toolTipFormatter}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default StorylandsLessonsTotals
